import React from 'react';
import { Minus, Plus, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

/**
 * SpeedControl — compact, mobile-friendly speed adjuster used by Flash + Chunk reading.
 *
 * Props
 *  - value:        current numeric value (ms per word for Flash, ms per chunk for Chunk)
 *  - onChange:     (n) => void  — new value to commit
 *  - min, max:     numeric bounds
 *  - step:         slider step
 *  - presets:      [{ label, value }] — preset chips (e.g. Slow / Normal / Fast / Turbo)
 *  - format:       (val) => string — how to show the current value (e.g. "250 WPM" or "1.5s / chunk")
 *  - disabled:     bool — disable all controls
 *  - accentClass:  tailwind classes for accent colour (e.g. "bg-purple-600", "bg-green-600")
 *  - testIdPrefix: string — prefix for data-testid attributes (e.g. "flash" / "chunk")
 */
const SpeedControl = ({
  value,
  onChange,
  min,
  max,
  step,
  presets = [],
  format = (v) => `${v}`,
  disabled = false,
  accentClass = 'bg-indigo-600',
  accentBorderClass = 'border-indigo-500',
  accentTextClass = 'text-indigo-300',
  testIdPrefix = 'speed',
}) => {
  const clamp = (n) => Math.min(max, Math.max(min, n));
  const nudge = (delta) => !disabled && onChange(clamp(value + delta));
  const activePreset = presets.find((p) => p.value === value)?.label;

  return (
    <div
      className="rounded-xl border border-slate-700/70 bg-slate-800/40 p-3 sm:p-4 space-y-3"
      data-testid={`${testIdPrefix}-speed-control`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Zap className={`w-4 h-4 shrink-0 ${accentTextClass}`} />
          <span className="text-xs uppercase tracking-widest font-semibold text-gray-400">
            Speed
          </span>
        </div>
        <span
          className={`text-base sm:text-lg font-bold ${accentTextClass} tabular-nums`}
          data-testid={`${testIdPrefix}-speed-value`}
        >
          {format(value)}
        </span>
      </div>

      {/* -/+ and slider row */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={() => nudge(-step)}
          disabled={disabled || value <= min}
          className={`h-9 w-9 shrink-0 border-slate-600 bg-slate-900/40 hover:${accentClass} hover:text-white`}
          data-testid={`${testIdPrefix}-speed-decrease`}
          aria-label="Decrease speed"
        >
          <Minus className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <Slider
            value={[value]}
            onValueChange={([val]) => !disabled && onChange(val)}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            data-testid={`${testIdPrefix}-speed-slider`}
          />
        </div>
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={() => nudge(step)}
          disabled={disabled || value >= max}
          className={`h-9 w-9 shrink-0 border-slate-600 bg-slate-900/40 hover:${accentClass} hover:text-white`}
          data-testid={`${testIdPrefix}-speed-increase`}
          aria-label="Increase speed"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Preset chips */}
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-speed-presets`}>
          {presets.map((p) => {
            const active = activePreset === p.label;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => !disabled && onChange(p.value)}
                disabled={disabled}
                data-testid={`${testIdPrefix}-speed-preset-${p.label.toLowerCase()}`}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
                  active
                    ? `${accentClass} text-white border ${accentBorderClass}`
                    : 'bg-slate-900/40 text-gray-300 border border-slate-600 hover:bg-slate-800'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpeedControl;
